import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import { exportToCsv } from '../../lib/exportCsv';
import { formatCount, formatPercent } from '../../lib/formatNumber';
import cloudAssetService, {
  CloudAuditReport,
} from '../../services/cloudAssetService';

const SEVERITY_VARIANT: Record<string, string> = {
  high: 'destructive',
  medium: 'secondary',
  low: 'outline',
};

/**
 * Cloud data-completeness audit (User Story 88).
 *
 * Findings are computed on read from the register, so this panel cannot drift
 * from the data it describes — there is no findings table to go stale. Every
 * count respects the caller's scope tier.
 */
export const CloudAuditPanel: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const [report, setReport] = useState<CloudAuditReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await cloudAssetService.getAuditReport());
    } catch (error: any) {
      showToast(
        error?.friendlyMessage || 'Failed to load the cloud audit',
        'error',
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = () => {
    if (!report) return;
    exportToCsv(
      `cloud-audit-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Finding',
        'Severity',
        'Resource',
        'Provider',
        'Provider Resource ID',
        'Environment',
        'Department',
      ],
      report.findings.flatMap((finding) =>
        finding.resources.map((resource) => [
          finding.title,
          finding.severity,
          resource.name,
          resource.provider,
          resource.providerResourceId,
          resource.environmentName ?? '',
          resource.departmentName ?? '',
        ]),
      ),
    );
  };

  if (loading) {
    return <p className="text-sm text-muted-foreground">Loading cloud audit…</p>;
  }
  if (!report) return null;

  const openFindings = report.findings.filter((finding) => finding.count > 0);

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Cloud Resources
            </div>
            <div className="mt-1 text-2xl font-semibold">
              {formatCount(report.totalResources)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Fully Recorded
            </div>
            <div className="mt-1 text-2xl font-semibold">
              {formatCount(report.compliantResources)}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              no findings against them
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Compliance
            </div>
            <div className="mt-1 text-2xl font-semibold">
              {formatPercent(report.compliancePercent)}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Gaps in the manually maintained cloud register. Every cost report
          built on it is only as good as these.
        </p>
        {hasPermission('reports.export') && (
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        )}
      </div>

      {openFindings.length === 0 ? (
        <div className="flex items-center gap-2 rounded-md border p-6 text-sm">
          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
          No findings — every cloud resource in your scope is fully recorded.
        </div>
      ) : (
        <div className="space-y-3">
          {openFindings.map((finding) => {
            const isOpen = expanded[finding.id];
            return (
              <Card key={finding.id}>
                <CardContent className="p-0">
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 p-4 text-left"
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [finding.id]: !prev[finding.id],
                      }))
                    }
                    aria-expanded={Boolean(isOpen)}
                  >
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{finding.title}</span>
                          <Badge
                            variant={SEVERITY_VARIANT[finding.severity] as any}
                          >
                            {finding.severity}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {formatCount(finding.count)} resource(s)
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {finding.description}
                        </p>
                      </div>
                    </div>
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0" />
                    )}
                  </button>

                  {isOpen && (
                    <div className="overflow-x-auto border-t">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="p-3 text-left font-medium">Resource</th>
                            <th className="p-3 text-left font-medium">Provider</th>
                            <th className="p-3 text-left font-medium">
                              Resource ID
                            </th>
                            <th className="p-3 text-left font-medium">Environment</th>
                            <th className="p-3 text-left font-medium">
                              Department
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {finding.resources.map((resource) => (
                            <tr key={resource.id} className="border-t">
                              <td className="p-3 font-medium">{resource.name}</td>
                              <td className="p-3 uppercase">{resource.provider}</td>
                              <td className="p-3 font-mono text-xs">
                                {resource.providerResourceId}
                              </td>
                              <td className="p-3">
                                {resource.environmentName ?? '—'}
                              </td>
                              <td className="p-3">
                                {resource.departmentName ?? '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CloudAuditPanel;
